import React from "react";
import { motion } from "framer-motion";
import { Brain, BookOpen, Search, ClipboardCheck, ArrowRight, CalendarCheck } from "lucide-react";

const CALENDLY = "https://calendly.com/wordgumbolearningco";

const SERVICES = [
  {
    key: "dyslexia-therapy",
    title: "Dyslexia Therapy Sessions",
    icon: Brain,
    accent: "bg-wg-purple text-white",
    chip: "bg-wg-purple/10 text-wg-purple",
    description:
      "Personalized, structured dyslexia therapy designed to support students with reading, spelling, decoding, fluency, and confidence. Sessions are tailored to each child's learning needs and use evidence-based strategies.",
  },
  {
    key: "reading-tutoring",
    title: "Reading Tutoring Services",
    icon: BookOpen,
    accent: "bg-wg-teal text-white",
    chip: "bg-wg-teal/10 text-wg-teal",
    description:
      "Supportive reading tutoring for children who need help strengthening foundational literacy skills, improving confidence, and building reading success. Especially focused on early readers in Kindergarten through 2nd grade.",
  },
  {
    key: "dyslexia-screeners",
    title: "Dyslexia Screeners",
    icon: Search,
    accent: "bg-wg-orange text-white",
    chip: "bg-wg-orange/10 text-wg-orange",
    description:
      "Dyslexia screeners help identify early signs of reading difficulty and provide families with helpful insight into whether further evaluation or support may be needed.",
  },
  {
    key: "dyslexia-core-assessments",
    title: "Full Dyslexia Core Assessments",
    icon: ClipboardCheck,
    accent: "bg-wg-green text-white",
    chip: "bg-wg-green/10 text-wg-green",
    description:
      "Comprehensive dyslexia core assessments provide a deeper understanding of a child's reading profile, learning strengths, and areas of need. These assessments help families make informed decisions about support and intervention.",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-wg-cream2/60"
    >
      {/* Decorative shapes */}
      <div aria-hidden="true" className="absolute -top-16 right-10 w-24 h-24 rounded-3xl bg-wg-yellow/30 rotate-12 hidden md:block" />
      <div aria-hidden="true" className="absolute bottom-12 left-6 w-16 h-16 rounded-full bg-wg-magenta/20 hidden md:block" />

      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-3.5 py-1.5 border border-wg-purple/15">
            <span className="w-1.5 h-1.5 rounded-full bg-wg-teal" />
            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-wg-purple">Services</span>
          </div>
          <h2 className="mt-5 font-heading font-extrabold text-3xl md:text-5xl tracking-tight text-wg-ink leading-tight">
            Reading support, <span className="wg-text-grad">stirred to perfection.</span>
          </h2>
          <p className="mt-5 text-lg text-wg-ink2 max-w-2xl">
            Evidence-based services for early readers and struggling learners — from gentle screeners
            to in-depth dyslexia therapy, every step is tailored to your child.
          </p>
        </motion.div>

        <div
          data-testid="services-grid"
          className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7"
        >
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`service-card-${s.key}`}
                className="group bg-white rounded-[1.6rem] p-7 md:p-8 border border-wg-purple/10 shadow-[0_8px_30px_rgb(74,29,82,0.05)] hover:shadow-[0_22px_50px_rgb(74,29,82,0.10)] hover:-translate-y-1.5 transition-all duration-300 flex flex-col"
              >
                <motion.div
                  whileHover={{ rotate: [0, -8, 8, 0], scale: 1.05 }}
                  transition={{ duration: 0.5 }}
                  className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl ${s.accent} shadow-md`}
                >
                  <Icon className="w-7 h-7" />
                </motion.div>
                <h3 className="mt-6 font-heading font-bold text-xl md:text-2xl leading-snug text-wg-ink">
                  {s.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-wg-ink2 flex-grow">
                  {s.description}
                </p>

                <div className="mt-6 flex flex-wrap gap-2">
                  <a
                    href="#consultation"
                    data-testid={`service-${s.key}-schedule-btn`}
                    className="inline-flex items-center gap-1.5 bg-wg-purple text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-wg-purpleDeep transition-colors"
                  >
                    <CalendarCheck className="w-4 h-4" />
                    Schedule
                  </a>
                  <a
                    href="#support"
                    data-testid={`service-${s.key}-learn-more-btn`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold ${s.chip} hover:opacity-90 transition-opacity`}
                  >
                    Learn more
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </motion.article>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <a
            href={CALENDLY}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="services-cta-btn"
            className="inline-flex items-center gap-2 bg-wg-orange text-white rounded-full px-7 py-4 font-semibold hover:bg-orange-600 hover:-translate-y-0.5 shadow-lg shadow-wg-orange/30 transition-all"
          >
            <CalendarCheck className="w-5 h-5" />
            Schedule a Free Consultation
          </a>
        </div>
      </div>
    </section>
  );
}
